import type { ReactNode } from 'react';
import { ApiError } from '../../api/client.ts';
import { cn } from './cn.ts';
import { Mono } from './layout.tsx';

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn(
        'inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
    />
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-1 py-8 text-sm text-tertiary">
      <Spinner />
      <span>{label}</span>
    </div>
  );
}

export type AlertTone = 'info' | 'success' | 'warning' | 'error';

const ALERT_TONES: Record<AlertTone, string> = {
  info: 'border-secondary bg-secondary text-secondary',
  success: 'border-success bg-success-primary text-success-primary',
  warning: 'border-warning bg-warning-primary text-warning-primary',
  error: 'border-error bg-error-primary text-error-primary',
};

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('rounded-lg border px-4 py-3 text-sm', ALERT_TONES[tone], className)}
    >
      {title ? <p className="font-semibold">{title}</p> : null}
      {children ? <div className={cn(title && 'mt-1')}>{children}</div> : null}
    </div>
  );
}

/**
 * A failed request, rendered as what it was.
 *
 * A 403 arrives carrying the resolution that produced it (`routes/errors.ts`
 * attaches the same `Decision` §4.8's views render), so a denial here can say
 * which grant stopped the caller rather than "forbidden" — which is the whole
 * point of §4.7 returning the winning grant instead of a verdict.
 */
export function ErrorState({ error, className }: { error: unknown; className?: string }) {
  if (!(error instanceof ApiError)) {
    const message = error instanceof Error ? error.message : 'Something went wrong.';
    return (
      <Alert tone="error" title="Request failed" className={className}>
        {message}
      </Alert>
    );
  }

  const denial = error.forbidden;

  return (
    <Alert tone="error" title={error.message} className={className}>
      <div className="flex flex-col gap-1.5">
        {denial ? (
          <p>
            <span className="font-mono">{denial.capability}</span> on {denial.subjectType}{' '}
            {denial.subjectId ? <Mono>{denial.subjectId}</Mono> : 'your role'}
            {denial.decision.grantId ? (
              <>
                {' '}
                — denied by a {denial.decision.principalType}-specific grant on{' '}
                {denial.decision.grantedOnType} <Mono>{denial.decision.grantedOnId}</Mono>, at
                distance {denial.decision.distance}.
              </>
            ) : (
              ' — no grant anywhere in the chain reaches this, so it is denied.'
            )}
          </p>
        ) : null}
        <p className="text-xs opacity-80">
          <span className="font-mono">{error.code}</span>
          {error.requestId ? (
            <>
              {' · request '}
              <span className="font-mono">{error.requestId}</span>
            </>
          ) : null}
        </p>
      </div>
    </Alert>
  );
}

/**
 * A whole page that could not load.
 *
 * Keeps the title and a way back, which a bare `ErrorState` does not: a 403 on
 * a detail route is a normal outcome here — the API resolves `space.grant`
 * against the subject, so someone who can see a node may legitimately not be
 * allowed to see who else can — and landing on a lone red box with no
 * navigation makes a correct refusal feel like a broken page.
 *
 * The link is a plain anchor rather than a router `Link`. A full navigation
 * re-runs the session guard, which is the right behaviour when the reason for
 * the failure might be that the session ended.
 */
export function PageError({
  error,
  title,
  back,
}: {
  error: unknown;
  title: string;
  back?: { href: string; label: string };
}) {
  return (
    <>
      <header className="flex flex-col gap-1 border-b border-secondary pb-5">
        {back ? (
          <a href={back.href} className="text-sm text-tertiary no-underline hover:text-primary">
            ← {back.label}
          </a>
        ) : null}
        <h1 className="text-display-xs font-semibold text-primary">{title}</h1>
      </header>
      <ErrorState error={error} />
    </>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <p className="text-sm font-semibold text-primary">{title}</p>
      {description ? <p className="max-w-md text-sm text-tertiary">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}
